interface Version {
  version: string;
  buildTime: string;
  buildId: string;
}

/**
 * Debug logger that logs in all environments for critical messages
 * and only in development for normal debug messages
 */
export const debugLog = (message: string, data?: any, forceLog: boolean = false): void => {
  if (process.env.NODE_ENV === 'development' || forceLog) {
    if (data) {
      console.log(`🔄 [Cache Buster] ${message}`, data);
    } else {
      console.log(`🔄 [Cache Buster] ${message}`);
    }
  }
};

/**
 * Clears all browser storage mechanisms that are accessible via JavaScript
 * This includes localStorage, sessionStorage, cookies, IndexedDB, and service worker caches
 * BUT preserves version tracking information to prevent refresh loops
 */
export const clearAllBrowserStorage = async (): Promise<void> => {
  try {
    debugLog('Starting browser storage clearing');

    // 1. Clear localStorage (but preserve version tracking)
    const localStorageItemCount = Object.keys(localStorage).length;

    // Save all possible version-related fields to be safe
    const currentVersion = localStorage.getItem('currentVersion');
    const currentBuildId = localStorage.getItem('currentBuildId');
    const lastRefresh = localStorage.getItem('lastRefresh');

    // Also save old values in case they're needed
    const versionToKeep = localStorage.getItem('appVersion');
    const buildIdToKeep = localStorage.getItem('appBuildId');
    const buildTimeToKeep = localStorage.getItem('appBuildTime');
    const lastRefreshTime = localStorage.getItem('cacheBusterLastRefresh');
    const lastDeploymentDate = localStorage.getItem('lastDeploymentDate');
    const processedVersion = localStorage.getItem('processedVersion');

    localStorage.clear();

    // Restore new version tracking first (priority)
    if (currentVersion) localStorage.setItem('currentVersion', currentVersion);
    if (currentBuildId) localStorage.setItem('currentBuildId', currentBuildId);
    if (lastRefresh) localStorage.setItem('lastRefresh', lastRefresh);

    // Restore old version tracking (for backward compatibility)
    if (versionToKeep) localStorage.setItem('appVersion', versionToKeep);
    if (buildIdToKeep) localStorage.setItem('appBuildId', buildIdToKeep);
    if (buildTimeToKeep) localStorage.setItem('appBuildTime', buildTimeToKeep);
    if (lastRefreshTime) localStorage.setItem('cacheBusterLastRefresh', lastRefreshTime);
    if (lastDeploymentDate) localStorage.setItem('lastDeploymentDate', lastDeploymentDate);
    if (processedVersion) localStorage.setItem('processedVersion', processedVersion);

    debugLog(`Cleared localStorage (${localStorageItemCount} items, preserved version tracking)`);

    // 2. Clear sessionStorage
    const sessionStorageItemCount = Object.keys(sessionStorage).length;
    sessionStorage.clear();
    debugLog(`Cleared sessionStorage (${sessionStorageItemCount} items)`);

    // 3. Clear all cookies except auth-related ones and version tracking
    const cookiesBefore = document.cookie.split(';').length;
    let cookiesCleared = 0;
    document.cookie.split(';').forEach((cookie) => {
      const eqPos = cookie.indexOf('=');
      const name = eqPos > -1 ? cookie.substr(0, eqPos).trim() : cookie.trim();

      // Skip authentication cookies to avoid forcing re-login
      // Also skip our version tracking cookies
      const isVersionCookie = [
        // New version cookies
        'currentVersion',
        'currentBuildId',
        'lastRefresh',
        // Old version cookies (for backward compatibility)
        'appVersion',
        'appBuildId',
        'appBuildTime',
        'lastDeploymentDate',
        'cacheBusterLastRefresh',
        'processedVersion'
      ].includes(name);

      if (name.includes('auth0') || name.includes('access_token') || name.includes('id_token') || isVersionCookie) {
        debugLog(`Preserving cookie: ${name}`);
        return;
      }

      document.cookie = name + '=;expires=Thu, 01 Jan 1970 00:00:00 GMT;path=/';
      document.cookie = name + '=;expires=Thu, 01 Jan 1970 00:00:00 GMT;path=/;domain=' + window.location.hostname;
      cookiesCleared++;
    });
    debugLog(`Cleared cookies (${cookiesCleared} cookies)`);

    // 4. Clear IndexedDB databases
    if (window.indexedDB) {
      try {
        const databases = await window.indexedDB.databases();
        debugLog(`Found ${databases.length} IndexedDB databases`);

        let cleared = 0;
        for (const database of databases) {
          if (database.name) {
            window.indexedDB.deleteDatabase(database.name);
            cleared++;
          }
        }
        debugLog(`Cleared ${cleared} IndexedDB databases`);
      } catch (err) {
        console.error('IndexedDB clear failed:', err);
      }
    }

    // 5. Clear CacheStorage (used by Service Workers)
    if ('caches' in window) {
      try {
        const cacheKeys = await caches.keys();
        debugLog(`Found ${cacheKeys.length} cache storages`);

        let cleared = 0;
        for (const name of cacheKeys) {
          await caches.delete(name);
          cleared++;
        }
        debugLog(`Cleared ${cleared} cache storages`);
      } catch (err) {
        console.error('Cache API clear failed:', err);
      }
    }

    // 6. Unregister all service workers
    if ('serviceWorker' in navigator) {
      try {
        const registrations = await navigator.serviceWorker.getRegistrations();
        debugLog(`Found ${registrations.length} service worker registrations`);

        let unregistered = 0;
        for (const registration of registrations) {
          await registration.unregister();
          unregistered++;
        }
        debugLog(`Unregistered ${unregistered} service workers`);
      } catch (err) {
        console.error('Service Worker unregister failed:', err);
      }
    }

    debugLog('All browser storage cleared successfully ✅');
  } catch (err) {
    console.error('Error during cache clearing:', err);
  }
};

/**
 * Forces a full page reload without changing the URL
 */
export const forcePageReload = (): void => {
  const bustCache = new Date().getTime();

  // Set the last refresh time to prevent refresh loops
  localStorage.setItem('cacheBusterLastRefresh', bustCache.toString());

  // Also set a cookie (1 hour expiry)
  const cookieExpires = new Date(Date.now() + 60 * 60 * 1000);
  document.cookie = `cacheBusterLastRefresh=${bustCache};expires=${cookieExpires.toUTCString()};path=/`;

  debugLog(`Forcing page reload without changing URL`, null, true);

  // Use location.reload() instead of changing the URL with cache parameters
  window.location.reload();
};

/**
 * Stores version information in both localStorage and cookies for cross-session persistence
 */
const storeVersionData = (version: string, buildId: string, buildTime?: string, timestamp?: number): void => {
  // Store in localStorage
  localStorage.setItem('appVersion', version);
  localStorage.setItem('appBuildId', buildId);
  if (buildTime) localStorage.setItem('appBuildTime', buildTime);

  // Only store timestamp if explicitly provided
  if (timestamp) {
    localStorage.setItem('lastDeploymentDate', timestamp.toString());
  }

  // Also store in cookies with a long expiration (1 year)
  const expires = new Date();
  expires.setFullYear(expires.getFullYear() + 1);

  document.cookie = `appVersion=${version};expires=${expires.toUTCString()};path=/`;
  document.cookie = `appBuildId=${buildId};expires=${expires.toUTCString()};path=/`;
  if (buildTime) document.cookie = `appBuildTime=${buildTime};expires=${expires.toUTCString()};path=/`;

  // Only store timestamp cookie if explicitly provided
  if (timestamp) {
    document.cookie = `lastDeploymentDate=${timestamp.toString()};expires=${expires.toUTCString()};path=/`;
  }

  debugLog('Version data stored in both localStorage and cookies for persistence');
};

/**
 * Gets a cookie value by name
 */
const getCookie = (name: string): string | null => {
  const match = document.cookie.match(new RegExp('(^| )' + name + '=([^;]+)'));
  return match ? match[2] : null;
};

/**
 * Checks if the application version is current by comparing to version.json
 * Returns true if versions match, false if a version mismatch is detected
 */
export const checkAppVersion = async (): Promise<boolean> => {
  try {
    // Record the check time
    const checkTime = Date.now();
    // Add multiple cache-busting parameters to force a fresh request
    const timestamp = checkTime;
    const random = Math.random().toString(36).substring(2, 15);

    // Always log version checks in production
    debugLog(`Checking app version at ${new Date(timestamp).toISOString()}`, null, true);

    // Use multiple cache-busting techniques
    // Try the timestamped version file first, then fallback to regular version.json
    const buildTimestamp = process.env.REACT_APP_BUILD_TIMESTAMP || '';
    const timestampedUrl = `/version-${buildTimestamp || timestamp}.json?t=${timestamp}&r=${random}&nocache=1`;
    const regularUrl = `/version.json?t=${timestamp}&r=${random}&nocache=1`;

    // Add cache-busting headers
    const fetchOptions: RequestInit = {
      headers: {
        'Cache-Control': 'no-cache, no-store, must-revalidate',
        Pragma: 'no-cache',
        Expires: '0'
      },
      // This is important to ensure we always go to network
      cache: 'no-store' as RequestCache
    };

    // Try to fetch the timestamped version file first
    let response, latestVersion;

    try {
      debugLog(`Attempting to fetch timestamped version file: ${timestampedUrl}`, null, true);
      response = await fetch(timestampedUrl, fetchOptions);

      if (!response.ok) {
        throw new Error('Timestamped version file not found, trying regular version.json');
      }

      latestVersion = await response.json();
      debugLog('Found timestamped version file', latestVersion, true);
    } catch (error) {
      // If that fails, try the regular version.json
      debugLog(`Falling back to regular version.json: ${regularUrl}`, null, true);
      response = await fetch(regularUrl, fetchOptions);

      if (!response.ok) {
        console.error('Failed to fetch any version info:', response.statusText);
        debugLog('⚠️ Failed to fetch any version info - will retry later', null, true);
        return true; // Assume version is ok to avoid unnecessary reloads
      }

      latestVersion = await response.json();
    }

    // Log version info in production too
    debugLog('Latest version from server:', latestVersion, true);

    // Step 1: Get stored version from localStorage AND cookies
    const storedVersion = localStorage.getItem('appVersion') || getCookie('appVersion');
    const storedBuildId = localStorage.getItem('appBuildId') || getCookie('appBuildId');

    // Step 2: Create the version identifiers
    const versionKey = `${latestVersion.version}|${latestVersion.buildId}`;
    const processedVersion = localStorage.getItem('processedVersion') || getCookie('processedVersion');

    // Check if processedVersion has an invalid format (like '0.1.0|0.1.0-timestamp')
    // Valid format should be in the pattern of 'ernie-4.3.25085.19|ernie-4.3.25085.19-27808'
    const processedVersionValid = processedVersion && processedVersion.split('|').length === 2 && processedVersion.includes(latestVersion.version.substring(0, 5)); // At least first 5 chars should match

    // If the processedVersion has invalid format, reset it
    if (processedVersion && !processedVersionValid) {
      debugLog(
        'Detected invalid processedVersion format - resetting',
        {
          processedVersion,
          expectedFormat: `${latestVersion.version}|${latestVersion.buildId}`
        },
        true
      );

      localStorage.removeItem('processedVersion');
      document.cookie = 'processedVersion=; expires=Thu, 01 Jan 1970 00:00:00 GMT; path=/';
    }

    // Get the cleaned up processedVersion
    const cleanedProcessedVersion = processedVersionValid ? processedVersion : null;

    // Step 3: Log version information
    debugLog(
      'Version comparison:',
      {
        stored: { version: storedVersion, buildId: storedBuildId },
        latest: { version: latestVersion.version, buildId: latestVersion.buildId },
        processedVersion: cleanedProcessedVersion,
        versionKey,
        alreadyProcessed: cleanedProcessedVersion === versionKey
      },
      true
    );

    // Step 4: Check if we've already processed this version
    if (cleanedProcessedVersion === versionKey) {
      debugLog(
        'This exact version has already been processed - skipping refresh',
        {
          processedVersion: cleanedProcessedVersion,
          versionKey,
          isMatch: true
        },
        true
      );

      // Double-check that the stored version is also updated to match
      if (storedVersion !== latestVersion.version || storedBuildId !== latestVersion.buildId) {
        debugLog(
          'Updating stored version to match current version',
          {
            old: { version: storedVersion, buildId: storedBuildId },
            new: { version: latestVersion.version, buildId: latestVersion.buildId }
          },
          true
        );

        // Update storage with the latest version info but don't trigger a refresh
        storeVersionData(latestVersion.version, latestVersion.buildId, latestVersion.buildTime);
      }

      return true;
    }

    // Log that we haven't processed this version yet
    debugLog(
      'This version has not been processed yet',
      {
        processedVersion: cleanedProcessedVersion,
        versionKey,
        isMatch: false
      },
      true
    );

    // Step 5: Check for critical mismatch in version identifiers
    const criticalMismatch = !storedVersion || !storedBuildId || storedVersion !== latestVersion.version || storedBuildId !== latestVersion.buildId;

    // Step 6: Handle version mismatch
    if (criticalMismatch) {
      debugLog(
        'VERSION MISMATCH DETECTED ⚠️',
        {
          stored: { version: storedVersion, buildId: storedBuildId },
          latest: { version: latestVersion.version, buildId: latestVersion.buildId },
          processedVersion: cleanedProcessedVersion,
          needsRefresh: true
        },
        true
      );

      // Update version storage
      storeVersionData(latestVersion.version, latestVersion.buildId, latestVersion.buildTime, latestVersion.timestamp || Date.now());

      // Mark this version as processed
      localStorage.setItem('processedVersion', versionKey);

      // Store in a cookie with a long expiration (1 year)
      const expires = new Date();
      expires.setFullYear(expires.getFullYear() + 1);
      document.cookie = `processedVersion=${versionKey};expires=${expires.toUTCString()};path=/`;

      // Set the last refresh time
      localStorage.setItem('cacheBusterLastRefresh', Date.now().toString());

      // Set a cookie for the last refresh time (1 hour expiry)
      const cookieExpires = new Date(Date.now() + 60 * 60 * 1000);
      document.cookie = `cacheBusterLastRefresh=${Date.now()};expires=${cookieExpires.toUTCString()};path=/`;

      // Log the mismatch
      console.log('🔄 [Cache Buster] VERSION MISMATCH DETECTED - App will refresh', {
        stored: { version: storedVersion, buildId: storedBuildId },
        latest: { version: latestVersion.version, buildId: latestVersion.buildId }
      });

      return false; // Version mismatch detected, trigger refresh
    }

    // Step 7: Handle version match
    debugLog('Version is current ✅', null, true);

    // Mark this version as processed
    localStorage.setItem('processedVersion', versionKey);

    // Store in a cookie with a long expiration (1 year)
    const expires = new Date();
    expires.setFullYear(expires.getFullYear() + 1);
    document.cookie = `processedVersion=${versionKey};expires=${expires.toUTCString()};path=/`;

    // Update storage to ensure consistency
    storeVersionData(latestVersion.version, latestVersion.buildId, latestVersion.buildTime);

    return true; // Version matches
  } catch (error) {
    console.error('Error checking app version:', error);
    debugLog('❌ Error checking version:', error, true);
    return true; // Assume version is ok to avoid unnecessary reloads
  }
};

/**
 * Adds a version query parameter to a URL to prevent caching
 */
export const addVersionToUrl = (url: string): string => {
  const version = localStorage.getItem('appVersion') || '0.1.0';
  const separator = url.includes('?') ? '&' : '?';
  return `${url}${separator}v=${version}`;
};

/**
 * Sets up a global fetch interceptor that adds cache-busting parameters to all requests
 * Call this function early in the application lifecycle
 */
export const setupFetchInterceptor = (): void => {
  // Store the original fetch function
  const originalFetch = window.fetch;

  debugLog('Setting up fetch interceptor for cache busting');

  // Keep track of modified URLs
  let modifiedUrls: Set<string> = new Set();

  // Log stats periodically in development
  if (process.env.NODE_ENV === 'development') {
    setInterval(() => {
      if (modifiedUrls.size > 0) {
        debugLog(`Fetch interceptor has modified ${modifiedUrls.size} URLs in the last minute`);
        // Reset the set
        modifiedUrls = new Set();
      }
    }, 60000); // Log every minute
  }

  // Override the fetch function with our own implementation
  window.fetch = async (input: RequestInfo | URL, init?: RequestInit): Promise<Response> => {
    try {
      // Only add cache busting to GET requests for static resources
      if (!init || init.method === undefined || init.method === 'GET') {
        // If input is a string URL
        if (typeof input === 'string') {
          // Only add version to non-API requests (likely static assets)
          if (!input.includes('/api/') && !input.includes('auth0.com')) {
            const originalUrl = input;
            input = addVersionToUrl(input);

            if (originalUrl !== input && process.env.NODE_ENV === 'development') {
              modifiedUrls.add(originalUrl);
              debugLog(`Added cache-busting to URL: ${originalUrl} → ${input}`);
            }
          }
        }
        // If input is a Request object
        else if (input instanceof Request) {
          const url = input.url;
          if (!url.includes('/api/') && !url.includes('auth0.com')) {
            const originalUrl = url;
            const newUrl = addVersionToUrl(url);

            // Create a new request with the modified URL
            input = new Request(newUrl, input);

            if (originalUrl !== newUrl && process.env.NODE_ENV === 'development') {
              modifiedUrls.add(originalUrl);
              debugLog(`Added cache-busting to Request: ${originalUrl} → ${newUrl}`);
            }
          }
        }
      }

      // Call the original fetch with our potentially modified input
      return originalFetch(input, init);
    } catch (error) {
      console.error('Error in fetch interceptor:', error);
      // Fall back to original fetch in case of error
      return originalFetch(input, init);
    }
  };

  debugLog('Fetch interceptor set up successfully');
};

/**
 * Debug function to manually trigger cache clearing and page reload.
 * This can be called from the browser console for testing purposes.
 *
 * Usage in browser console: window.manualCacheBust()
 */
export const setupDebugTriggers = (): void => {
  if (process.env.NODE_ENV === 'development') {
    debugLog('Setting up manual cache busting trigger for debugging');

    // Add a global function to manually trigger cache busting
    (window as any).manualCacheBust = async () => {
      debugLog('🔥 MANUAL CACHE BUST TRIGGERED 🔥');
      debugLog('Clearing all browser storage');
      await clearAllBrowserStorage();

      // Clear version from localStorage to force a mismatch
      localStorage.removeItem('appVersion');
      localStorage.removeItem('appBuildId');
      // Set justUpdated to false to allow cache busting to happen
      localStorage.setItem('justUpdated', 'false');

      debugLog('Forcing page reload');
      forcePageReload();
    };

    // Add a function to check current version info
    (window as any).checkVersionInfo = async () => {
      debugLog('Manually checking version info');
      const timestamp = new Date().getTime();

      try {
        const response = await fetch(`/version.json?t=${timestamp}`);
        if (!response.ok) {
          debugLog('❌ Failed to fetch version info');
          return;
        }

        const serverVersion = await response.json();
        const storedVersion = localStorage.getItem('appVersion');
        const storedBuildId = localStorage.getItem('appBuildId');
        const justUpdated = localStorage.getItem('justUpdated');

        debugLog('Version comparison:', {
          server: serverVersion,
          client: {
            version: storedVersion,
            buildId: storedBuildId,
            justUpdated: justUpdated
          },
          match: storedVersion === serverVersion.version && storedBuildId === serverVersion.buildId
        });
      } catch (error) {
        debugLog('❌ Error checking version:', error);
      }
    };

    // Add a function to reset the justUpdated flag
    (window as any).resetUpdateFlag = () => {
      debugLog('Manually resetting justUpdated flag');
      localStorage.removeItem('justUpdated');
      debugLog('Flag reset - next version check will proceed normally');
    };

    debugLog('Debug triggers set up. Use window.manualCacheBust(), window.checkVersionInfo(), or window.resetUpdateFlag() in console');
  }
};
